import { Button, Box } from 'theme-ui';
import { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';

import ImgLoader from '../ImgLoader';
import useWindowSize from '~/hooks/components/use-window-size';
import ChevronLeftIcon from '~/assets/images/icons/chevron-left.svg';
import ChevronRightIcon from '~/assets/images/icons/chevron-right.svg';
import CloseIcon from '~/assets/images/icons/close.svg';
import CarouselVideoPlayer from './VideoPlayer';
import Slider from 'react-slick';
import '~/styles/custom-slick.css';
import '~/styles/custom-slick-theme.css';
import SliderButton from './SliderButton';

const ImageCarousel = ({
  imgLoaderSx,
  hoverPlay,
  disableFull,
  dots,
  images,
  initialIndex = 0,
  setIsZoomed,
  fromPdp = false,
  forwardSx = {},
  setActiveMedia,
  badge,
}) => {
  const { width } = useWindowSize();
  const [isFullScreen] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  // Use the actual height of the window to workaround mobile Safari's toolbar
  const thatWindow = typeof window !== 'undefined' ? window : undefined;
  const vh = thatWindow?.innerHeight;

  useEffect(() => {
    if (!isFullScreen) {
      document.documentElement.style.overflow = 'initial';
      document.ontouchmove = () => true;
    } else {
      setTimeout(() => {
        document.documentElement.scrollTo(0, 0);
      }, 100);
      document.documentElement.style.overflow = 'hidden';
      document.ontouchmove = (e) => e.preventDefault();
    }
  }, [isFullScreen]);

  useEffect(() => {
    setActiveSlideIndex(initialIndex);
  }, [initialIndex]);

  return (
    <Box
      as="section"
      sx={{
        position: 'relative',
        height: disableFull ? ['50rem', '65rem'] : ['37rem', fromPdp ? '100vh' : '69rem', fromPdp ? '100vh' : '79rem'],

        '.slick-slider': {
          backgroundColor: 'white',
          height: isFullScreen ? (vh ? `${vh}px` : '100vh') : '100%',

          transform: isFullScreen
            ? 'translateY(calc((var(--products-nav-height) + var(--nav-height) + 2px) * -1))'
            : 'none',
          paddingBottom: isFullScreen ? 'env(safe-area-inset-bottom)' : 0,
          zIndex: isFullScreen ? 20 : 0,
          touchAction: 'pan-y pinch-zoom !important',
          boxShadow: isFullScreen ? '0 0 0 100vh rgba(0, 0, 0, 0.2)' : 'none',
        },
        '.slick-slider .slick-dots': {
          display: 'flex !important',
          flexFlow: 'row nowrap',
          justifyContent: 'center',
          alignItems: 'center',
          bottom: '2.6rem',
          filter: 'drop-shadow(0 0 0.04rem white)',
        },
        '.slick-slider .slick-list': { height: '100%' },
        '.slick-slider .slick-list .slick-track': { height: '100%' },
        '.slick-slider .slick-list .slick-track .slick-slide': { height: '100%', position: 'relative' },
        '.slick-slider .slick-list .slick-track .slick-slide > div': {
          height: '100%',
          position: width >= 576 && fromPdp ? 'absolute' : 'relative',
          top: width >= 576 && fromPdp ? '51%' : null,
          left: width >= 576 && fromPdp ? '50%' : null,
          transform: width >= 576 && fromPdp ? 'translate(-50%, -50%)' : null,
        },
        '.gatsby-plugin-image-wrapper': { height: '100%' },
        '.slick-slider .slick-dots li': {
          width: '0.8rem',
          height: '0.8rem',
          margin: 0,
          button: {
            width: '0.8rem',
            height: '0.8rem',

            padding: 0,
            borderRadius: '50%',
            backgroundColor: dots ?? 'black',
            opacity: 0.2,
            '::before': {
              content: '""',
              display: 'none',
            },
          },
          '&.slick-active button': {
            opacity: 1,
          },
          ':not(:last-of-type)': {
            marginRight: '0.8rem',
          },
        },
        ...forwardSx,
      }}
    >
      <Slider
        {...{
          dots: true,
          arrows: disableFull ? false : true,
          infinite: true,
          speed: 500,
          slidesToShow: 1,
          slidesToScroll: 1,
          autoplay: disableFull ?? false,
          autoplaySpeed: 4000,
          pauseOnHover: hoverPlay ?? true,
          cssEase: 'ease',
          initialSlide: initialIndex,
          fade: true,
          afterChange: (index) => {
            if (setActiveMedia) setActiveMedia(images[index]);
          },
          beforeChange: (_, newIndex) => {
            if (setActiveSlideIndex) setActiveSlideIndex(newIndex);
          },
        }}
        className={isFullScreen ? 'is-fullscreen' : ''}
        prevArrow={
          <SliderButton
            aria-label="Previous image"
            forwardSx={{ left: ['0rem', null, fromPdp ? 'calc(50% - min(30vw, 60vh))' : '2rem'] }}
          >
            <ChevronLeftIcon />
          </SliderButton>
        }
        nextArrow={
          <SliderButton
            aria-label="Next image"
            forwardSx={{ right: ['0rem', null, fromPdp ? 'calc(50% - min(30vw, 60vh))' : '2rem'] }}
          >
            <ChevronRightIcon />
          </SliderButton>
        }
      >
        {images.map((image, index) =>
          width >= 576 && fromPdp ? (
            <Fragment key={image.id}>
              <Button
                variant="bare"
                onClick={() => !image.video && setIsZoomed(false)}
                sx={{
                  '--strokecolor': 'black',
                  cursor: !image.video ? 'zoom-out' : 'pointer',
                  position: 'relative',
                  top: [null, '50%'],
                  transform: [null, 'translateY(-50%)'],
                  zIndex: 11,
                }}
              >
                {fromPdp && (
                  <Button
                    onClick={() => setIsZoomed(false)}
                    variant="bare"
                    sx={{
                      position: 'absolute',
                      top: '3rem',
                      right: '3rem',
                      zIndex: 101,
                      display: ['none', 'block'],
                      filter: 'drop-shadow(0 0 0.04rem white)',
                    }}
                  >
                    <CloseIcon />
                  </Button>
                )}
                {/image/i.test(image?.file?.contentType) && (
                  <ImgLoader
                    key={image.id}
                    image={image}
                    forwardSx={{
                      aspectRatio: '1/1',
                      objectFit: 'cover',
                      height: 'auto',
                      maxWidth: `${image?.file?.details?.image?.width}px`,
                      width: ['100%', 'min(100vh, 100vw)', 'min(50vw, 100vh)'],
                      ...imgLoaderSx,
                    }}
                    bgColor="white"
                    caption={image.description}
                    badge={index === 0 ? badge : undefined}
                  />
                )}
                {image.video && (
                  <CarouselVideoPlayer key={image.id} video={image} isCurrentSlide={activeSlideIndex === index} />
                )}
              </Button>
            </Fragment>
          ) : (
            <Fragment key={image.id}>
              {/image/i.test(image?.file?.contentType) && (
                <ImgLoader
                  image={image}
                  bgColor="white"
                  forwardSx={{
                    aspectRatio: '1/1',
                    objectFit: 'cover',
                    height: '100%',
                    width: '100%',
                    ...imgLoaderSx,
                  }}
                />
              )}
              {image.video && (
                <CarouselVideoPlayer key={image.id} video={image} isCurrentSlide={activeSlideIndex === index} />
              )}
            </Fragment>
          )
        )}
      </Slider>
    </Box>
  );
};

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      gatsbyImageData: PropTypes.object.isRequired,
    })
  ).isRequired,
};

export default ImageCarousel;
